import React from 'react';
import { Card, CardContent, Typography, Grid, TextField, MenuItem, Button, Tooltip, Box, Chip } from '@mui/material';
import InfoIcon from '@iconify/icons-eva/info-fill';
import { getAvailableFilters } from './analytics.constants';
import { Loader } from 'src/components/Loader';
 // hooks
import { useDropdown } from 'src/hooks/useDropdown.hook';

const AnalyticsFilters = ({
  title = "Filters & Date Range",
  analyticsType = "dashboard",
  timeRange,
  onTimeRangeChange,
  isCustomSelect,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  dateOptions = [],
  onApply,
  showPartnerFilter = true,
  showTimeFilter = true,
  showCustomDates = true,
  showApplyButton = true,
  buttonSize = "small",
  showConfigurationInfo = true,
  analyticsTypes = [],
  analyticsMetadata = {},
  loading = false,
  partnerId,
  onPartnerChange
}) => {
  // Get filter options for the specified analytics type
  const filterOptions = getAvailableFilters(analyticsType, analyticsTypes || []);
  const cacheInfo = analyticsMetadata?.cacheInfo;
  const { data: ocpiPartners } = useDropdown("ocpi_partners");

  // Build dynamic filter fields based on configuration
  const renderDynamicFilters = () => {
    if (!filterOptions || filterOptions.length === 0) return null;

    return filterOptions.map((filterKey) => {
      switch (filterKey) {
        case 'startDate':
          return (
            <Grid item xs={12} sm={2} key={filterKey}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={customStartDate || ''}
                onChange={(e) => onCustomStartDateChange?.(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText={analyticsMetadata?.availableFilters?.dateRange?.description || 'Start date for analytics'}
              />
            </Grid>
          );
        
        case 'endDate':
          return (
            <Grid item xs={12} sm={2} key={filterKey}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={customEndDate || ''}
                onChange={(e) => onCustomEndDateChange?.(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText={analyticsMetadata?.availableFilters?.dateRange?.description || 'End date for analytics'}
              />
            </Grid>
          );
        
        case 'partnerId':
          return (
            <Grid item xs={12} sm={3} key={filterKey}>
              <TextField
                select
                fullWidth
                label="Partner"
                defaultValue="all"
                size="small"
                helperText={analyticsMetadata?.availableFilters?.partnerId?.description || 'Filter by partner'}
              >
                <MenuItem value="all">All Partners</MenuItem>
                {ocpiPartners && ocpiPartners.map((partner, index) => (
                  <MenuItem key={index} value={index}>
                    {partner.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          );
        
        default:
          return (
            <Grid item xs={12} sm={3} key={filterKey}>
              <TextField
                fullWidth
                label={filterKey}
                size="small"
                helperText={`Filter by ${filterKey}`}
                onChange={(e) => {
                  // Handle custom filter changes if needed
                  console.log(`${filterKey} changed:`, e.target.value);
                }}
              />
            </Grid>
          );
      }
    });
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {title}
          </Typography>
          
          {showConfigurationInfo && cacheInfo && cacheInfo.ttl && (
            <Tooltip title={cacheInfo.description || 'Cache information'}>
              <Chip
                icon={<InfoIcon />}
                label={`Cache: ${cacheInfo.ttl}`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={3} alignItems="center">
          {/* Partner Filter */}
          {showPartnerFilter && (
            <Grid item xs={12} sm={3}>
              <TextField 
                select 
                fullWidth 
                label="Partner" 
                value={partnerId}
                onChange={(e) => onPartnerChange?.(e.target.value)}
                size="small" 
                helperText={analyticsMetadata?.availableFilters?.partnerId?.description || 'Filter by partner'}
              >
                <MenuItem value="all">All Partners</MenuItem>
                {ocpiPartners && 
                ocpiPartners.map((partner, index) => (
                  <MenuItem key={index} value={partner.id || index}>
                    {partner.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Time Range Filter */}
          {showTimeFilter && (
            <Grid item xs={12} sm={3}>
              <TextField 
                select 
                fullWidth 
                label="Time Range" 
                value={timeRange} 
                onChange={(e) => onTimeRangeChange(e.target.value)} 
                size="small"
                helperText={analyticsMetadata?.availableFilters?.dateRange?.description || 'Select time range'}
              >
                {dateOptions.map((item) => (
                  <MenuItem key={item.id} value={item.value}>
                    {item.value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* Custom Date Fields */}
          {showCustomDates && isCustomSelect && (
            <>
              <Grid item xs={12} sm={2}>
                <TextField 
                  fullWidth 
                  type="date" 
                  label="Start Date" 
                  value={customStartDate || ''} 
                  onChange={(e) => onCustomStartDateChange(e.target.value)} 
                  size="small" 
                  InputLabelProps={{ shrink: true }}
                  helperText={`Format: ${analyticsMetadata?.supportedFormats?.date || 'YYYY-MM-DD'}`}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField 
                  fullWidth 
                  type="date" 
                  label="End Date" 
                  value={customEndDate || ''} 
                  onChange={(e) => onCustomEndDateChange(e.target.value)} 
                  size="small" 
                  InputLabelProps={{ shrink: true }}
                  helperText={`Format: ${analyticsMetadata?.supportedFormats?.date || 'YYYY-MM-DD'}`}
                />
              </Grid>
            </>
          )}

          {/* Dynamic Filters from Configuration */}
          {renderDynamicFilters()}

          {/* Apply Button */}
          {showApplyButton && (
            <Grid item xs={12} sm={2}>
              <Button 
                variant="contained" 
                fullWidth 
                size={buttonSize} 
                onClick={onApply}
              >
                Apply
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Filter Information */}
        {filterOptions && filterOptions.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Available filters for {analyticsType}: {filterOptions.join(', ')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsFilters;
